import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp, useAppState } from '../../../context/AppContext';
import NavigationService from '../../../utils/history';
import UserAPI from '../../../api/user';
import RoleAPI from '../../../api/role';
import useErrorHandler from '../../../shared/hooks/useErrorHandler';
import { parseDataBySelect } from '../../../shared/utils/parseDataBySelect';
import { IconButton } from '../../../insfrastructure/ui/button';

/**
 * Hook personalizado para manejar usuarios
 * Reemplaza UserController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar usuarios
 */
export const useUsers = (props) => {
    const { dispatch, notify } = useApp();
    const usersState = useAppState('users');
    const vuserState = useAppState('vuser');
    const rolesState = useAppState('roles');
    const handleError = useErrorHandler();

    const [users, setUsers] = useState(usersState || []);
    const [view, setView] = useState(vuserState || null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [rol, setRol] = useState(null);
    const [confirmCreate, setConfirmCreate] = useState(false);
    const [removeModal, setRemoveModal] = useState(false);
    const [idRemoveModal, setIdRemoveModal] = useState(null);
    const [edit, setEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dataLoadedRef = useRef(false);
    const currentIdRef = useRef(null);

    // No usar useRoles aquí para evitar dependencias circulares

    // Columnas para la tabla
    const columns = [
        { key: 'name', label: 'Nombre' },
        { key: 'email', label: "Correo eléctronico" },
        { key: 'role', label: "Rol de usuario" },
        { key: 'actions', label: 'Acciones' }
    ];

    // Custom elements para la tabla
    const customElements = {
        actions: (user) => (
            <div>
                <IconButton onClick={() => goView(user._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => openRemoveModal(user._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.33 16.5H13.66" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 12.5H14.5" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        ),
    };

    /**
     * Navegar a vista de usuario
     */
    const goView = useCallback((id) => {
        NavigationService.navigateTo(`/config/users/${id}`);
    }, []);

    /**
     * Navegar a crear nuevo usuario
     */
    const goNew = useCallback(() => {
        NavigationService.navigateTo(`/config/users/new`);
    }, []);

    /**
     * Navegar atrás
     */
    const goBack = useCallback(() => {
        NavigationService.navigateBack();
    }, []);

    /**
     * Abrir modal de eliminación
     */
    const openRemoveModal = useCallback((id) => {
        setIdRemoveModal(id);
        setRemoveModal(true);
    }, []);

    // Sincronizar con estado global
    useEffect(() => {
        if (usersState) setUsers(usersState);
    }, [usersState]);

    useEffect(() => {
        if (vuserState) setView(vuserState);
    }, [vuserState]);

    // Actualizar campos del formulario cuando cambia la vista
    useEffect(() => {
        if (view) {
            setName(view?.name || '');
            setEmail(view?.email || '');
            // Parsear el rol para el select (formato {label, value})
            const rolParse = view?.role ? { label: view.role, value: view.role } : null;
            setRol(rolParse);
        } else {
            // Limpiar campos cuando no hay vista
            setName('');
            setEmail('');
            setRol(null);
        }
    }, [view]);

    /**
     * Cargar roles
     */
    const loadRoles = useCallback(async () => {
        try {
            const res = await RoleAPI.getAll();
            const rolesList = Array.isArray(res) ? res : [];
            dispatch({ type: 'SET_ROLES', payload: rolesList });
            return rolesList;
        } catch (error) {
            handleError(error, 'Error al cargar los roles');
            throw error;
        }
    }, [dispatch, handleError]);



    /**
     * Obtener todos los usuarios
     */
    const getAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await UserAPI.getAll();
            const usersList = Array.isArray(res)
                ? res.map(e => parseDataBySelect(e, e.name || 'Sin nombre'))
                : [];

            dispatch({ type: 'SET_USERS', payload: usersList });
            setUsers(usersList);
            return usersList;
        } catch (error) {
            handleError(error, 'Error al obtener los usuarios');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleError]);

    /**
     * Cargar datos iniciales (usuarios y roles)
     */
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            await Promise.all([
                getAll(),
                loadRoles()
            ]);
        } catch (error) {
            handleError(error, 'Error al cargar los datos');
        } finally {
            setIsLoading(false);
        }
    }, [getAll, loadRoles, handleError]);

    /**
     * Obtener usuario por ID
     */
    const getById = useCallback(async (id) => {
        if (!id) return null;

        setIsLoading(true);
        try {
            // Intentar obtener del estado local primero (optimización)
            const currentUsers = usersState || [];
            const foundUser = currentUsers.find(e => e._id === id);

            if (foundUser) {
                dispatch({ type: 'SET_VUSER', payload: foundUser });
                setView(foundUser);
                setEdit(true);
            }

            // Siempre obtener del servidor para asegurar datos actualizados
            const res = await UserAPI.getById(id);
            const userData = res;

            dispatch({ type: 'SET_VUSER', payload: userData });
            setView(userData);
            setEdit(true);
            return userData;
        } catch (error) {
            handleError(error, `Error al obtener el usuario con ID: ${id}`);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [usersState, dispatch, handleError]);

    /**
     * Crear nuevo usuario
     */
    const create = useCallback(async (data) => {
        setIsLoading(true);
        try {
            const res = await UserAPI.create(data);
            await getAll(); // Refrescar lista
            notify({
                title: "Éxito!",
                type: "success",
                text: "Usuario creado correctamente"
            });
            return res;
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Error al crear el usuario";
            handleError(error, errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Actualizar usuario
     */
    const update = useCallback(async (id, data) => {
        setIsLoading(true);
        try {
            const res = await UserAPI.update(id, data);
            await getAll(); // Refrescar lista
            notify({
                title: "Éxito!",
                type: "success",
                text: "Usuario actualizado correctamente"
            });
            return res;
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Error al actualizar el usuario";
            handleError(error, errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Eliminar usuario
     */
    const remove = useCallback(async (id) => {
        if (!id) {
            notify({
                title: "Error",
                type: "error",
                text: "No se ha seleccionado un usuario para eliminar"
            });
            return;
        }

        setIsLoading(true);
        try {
            const res = await UserAPI.remove(id);
            await getAll(); // Refrescar lista
            setRemoveModal(false);
            setIdRemoveModal(null);
            notify({
                title: "Éxito!",
                type: "success",
                text: "Usuario eliminado correctamente"
            });
            return res;
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || "Error al eliminar el usuario";
            handleError(error, errorMessage);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Guardar usuario (create o update según edit)
     */
    const save = useCallback(async () => {
        // Validar que los campos requeridos estén llenos
        if (!name || !email || !rol) {
            notify({
                title: "Error",
                type: "error",
                text: "Por favor complete todos los campos requeridos"
            });
            return;
        }

        // Obtener el valor del rol (puede ser rol.value, rol.name, o rol directamente si es string)
        const roleValue = rol?.value || rol?.name || rol;

        const data = {
            email,
            role: roleValue,
            name,
            password: "admin01" // TODO: Implementar generación de password segura o campo de password
        };

        setConfirmCreate(false);

        try {
            if (edit && view?._id) {
                await update(view._id, data);
            } else {
                await create(data);
                // Redirigir a la lista después de crear
                setTimeout(() => {
                    goBack();
                }, 1000);
            }
        } catch (error) {
            // Errores ya manejados por handleError en create/update
        }
    }, [name, email, rol, edit, view, create, update, notify, goBack]);

    /**
     * Eliminar usuario (wrapper para mantener compatibilidad)
     */
    const handleRemove = useCallback(async () => {
        if (!idRemoveModal) {
            notify({
                title: "Error",
                type: "error",
                text: "No se ha seleccionado un usuario para eliminar"
            });
            return;
        }
        await remove(idRemoveModal);
    }, [idRemoveModal, remove, notify]);



    // Cargar datos al montar o cuando cambie el ID
    useEffect(() => {
        const id = props.match?.params?.id;
        
        // Si el ID cambió, resetear el flag de carga
        if (currentIdRef.current !== id) {
            dataLoadedRef.current = false;
        }
        
        // Evitar cargar si ya se cargó para el mismo ID
        if (dataLoadedRef.current && currentIdRef.current === id) {
            return;
        }
        
        let isMounted = true;
        currentIdRef.current = id;
        
        const loadInitialData = async () => {
            if (!isMounted) return;
            
            try {
                // Cargar datos solo si no se han cargado antes
                if (!dataLoadedRef.current) {
                    setIsLoading(true);
                    await Promise.all([
                        UserAPI.getAll().then(res => {
                            const usersList = Array.isArray(res)
                                ? res.map(e => parseDataBySelect(e, e.name || 'Sin nombre'))
                                : [];
                            dispatch({ type: 'SET_USERS', payload: usersList });
                            setUsers(usersList);
                        }),
                        RoleAPI.getAll().then(res => {
                            const rolesList = Array.isArray(res) ? res : [];
                            dispatch({ type: 'SET_ROLES', payload: rolesList });
                        })
                    ]);
                    dataLoadedRef.current = true;
                    setIsLoading(false);
                }
                
                if (id && isMounted) {
                    setIsLoading(true);
                    try {
                        const res = await UserAPI.getById(id);
                        dispatch({ type: 'SET_VUSER', payload: res });
                        setView(res);
                        setEdit(true);
                    } catch (error) {
                        handleError(error, `Error al obtener el usuario con ID: ${id}`);
                    } finally {
                        setIsLoading(false);
                    }
                } else if (isMounted) {
                    setEdit(false);
                }
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error);
                setIsLoading(false);
            }
        };
        
        loadInitialData();
        
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.match?.params?.id, dispatch, handleError]); // Solo cuando cambia el ID

    return {
        // Datos
        users,
        view,
        roles: rolesState || [],
        columns,
        customElements,

        // Estado del formulario
        name,
        setName,
        email,
        setEmail,
        rol,
        setRol,
        edit,
        setEdit,

        // Modales
        confirmCreate,
        setConfirmCreate,
        removeModal,
        setRemoveModal,
        idRemoveModal,
        setIdRemoveModal,

        // Estado de carga
        isLoading,

        // Funciones CRUD
        getAll,
        getById,
        create,
        update,
        remove,
        save,
        handleRemove,
        loadData,

        // Navegación
        goView,
        goNew,
        goBack,
        openRemoveModal,

        // Helpers
        refresh: getAll,
    };
};

export default useUsers;

